/**
 * Button — the only button in the multi-agent UI.
 *
 * Variants:
 *   primary — solid accent (one CTA per surface — Send, Approve, etc.)
 *   ghost   — text-only; the default for everything else
 *   danger  — destructive (Stop, Cancel run)
 *
 * Sizes:
 *   sm — 26px, used in toolbars / inline chips
 *   md — 32px (default)
 *   lg — 40px (hero CTA on empty state)
 *
 * Icon-only buttons (rail, header chrome) use <IconButton> instead.
 */
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { forwardRef } from "react";
import { Loader2 } from "lucide-react";

type Variant = "primary" | "ghost" | "danger";
type Size = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  busy?: boolean;
  leadingIcon?: ReactNode;
  children?: ReactNode;
}

const BTN_BASE =
  "inline-flex items-center gap-1.5 rounded-md border border-transparent font-medium cursor-pointer transition-[background-color,color,border-color,filter] duration-150 ease-[cubic-bezier(0.25,0.46,0.45,0.94)] disabled:cursor-not-allowed disabled:opacity-50";

const SIZE_CLASS: Record<Size, string> = {
  sm: "h-[26px] px-2 text-[11px]",
  md: "h-8 px-3 text-[15px]",
  lg: "h-10 px-4 text-[15px]",
};

const VAR_CLASS: Record<Variant, string> = {
  primary: "bg-primary text-primary-foreground hover:brightness-110",
  ghost: "bg-transparent text-muted-foreground hover:bg-muted/40 hover:text-foreground",
  danger: "bg-transparent text-muted-foreground hover:bg-muted/40 hover:text-destructive",
};

export const ICON_BTN_CLASS =
  "inline-flex h-8 w-8 items-center justify-center rounded-md border-0 bg-transparent text-muted-foreground cursor-pointer transition-[color,background-color] duration-150 ease-[cubic-bezier(0.25,0.46,0.45,0.94)] hover:bg-muted/40 hover:text-foreground aria-pressed:bg-primary/10 aria-pressed:text-foreground";

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "ghost", size = "md", busy = false, leadingIcon, children, className = "", disabled, ...rest },
  ref,
) {
  const cls = [BTN_BASE, VAR_CLASS[variant], SIZE_CLASS[size], className].filter(Boolean).join(" ");
  return (
    <button ref={ref} className={cls} disabled={disabled || busy} {...rest}>
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : leadingIcon}
      {children}
    </button>
  );
});

export const IconButton = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement> & { active?: boolean }>(
  function IconButton({ active, className = "", children, ...rest }, ref) {
    return (
      <button
        ref={ref}
        className={`${ICON_BTN_CLASS} ${className}`.trim()}
        aria-pressed={active ? "true" : undefined}
        {...rest}
      >
        {children}
      </button>
    );
  },
);
